import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import './FloatingNameTitle.css';
import {
  getIntersectionRect,
  mergeRects,
  translateRect,
  type RectLike,
} from '../../lib/utils';
import {
  collectVisibleTextNodes,
  getCandidateCharacterRects,
  type CharacterRectCacheEntry,
} from '../../lib/nameOverlapDom';

const OVERLAP_PADDING = 8;
const MIN_OVERLAP_WIDTH = 1.5;
const MIN_OVERLAP_HEIGHT = 3;
const RECT_MERGE_TOLERANCE = 1;

type FloatingNameTitleProps = {
  text: string;
  isVisible: boolean;
  onClick?: () => void;
};

type MaskStyle = React.CSSProperties & {
  WebkitMaskImage?: string;
  WebkitMaskPosition?: string;
  WebkitMaskRepeat?: string;
  WebkitMaskSize?: string;
};

type AnimatedCharacterStyle = React.CSSProperties & {
  '--floating-name-hop-index': number;
  '--floating-name-rest-y': string;
  '--floating-name-rest-rotate': string;
};

const RESTING_OFFSETS = [
  '0em',
  '0.1em',
  '-0.035em',
  '0.065em',
  '-0.055em',
  '0.085em',
  '-0.02em',
];
const RESTING_ROTATIONS = [
  '-0.5deg',
  '1.5deg',
  '-1deg',
  '0.75deg',
  '-1.25deg',
  '1deg',
  '-0.4deg',
];

const renderAnimatedText = (text: string) => {
  let hopIndex = 0;

  return Array.from(text).map((character, characterIndex) => {
    const isWhitespace = /\s/.test(character);
    const currentHopIndex = hopIndex;

    if (!isWhitespace) {
      hopIndex += 1;
    }

    const directionClass =
      currentHopIndex % 2 === 0
        ? 'floating-name-char--down'
        : 'floating-name-char--up';
    const style = isWhitespace
      ? undefined
      : ({
          '--floating-name-hop-index': currentHopIndex,
          '--floating-name-rest-y':
            RESTING_OFFSETS[currentHopIndex % RESTING_OFFSETS.length],
          '--floating-name-rest-rotate':
            RESTING_ROTATIONS[currentHopIndex % RESTING_ROTATIONS.length],
        } as AnimatedCharacterStyle);

    return (
      <span
        key={`${characterIndex}-${character}`}
        className={`floating-name-char${
          isWhitespace ? ' floating-name-char--space' : ` ${directionClass}`
        }`}
        style={style}
      >
        {isWhitespace ? (
          character
        ) : (
          <span className="floating-name-char-glyph">{character}</span>
        )}
      </span>
    );
  });
};

const toPlainRect = (rect: DOMRectReadOnly): RectLike => ({
  top: rect.top,
  right: rect.right,
  bottom: rect.bottom,
  left: rect.left,
  width: rect.width,
  height: rect.height,
});

const areRectListsEqual = (a: RectLike[], b: RectLike[]) => {
  if (a.length !== b.length) {
    return false;
  }

  for (let index = 0; index < a.length; index += 1) {
    const current = a[index];
    const next = b[index];

    if (
      current.top !== next.top ||
      current.right !== next.right ||
      current.bottom !== next.bottom ||
      current.left !== next.left ||
      current.width !== next.width ||
      current.height !== next.height
    ) {
      return false;
    }
  }

  return true;
};

const buildOverlayMaskStyle = (rects: RectLike[]): MaskStyle | undefined => {
  if (rects.length === 0) {
    return undefined;
  }

  const maskImage = rects.map(() => 'linear-gradient(#000 0 0)').join(', ');
  const maskSize = rects
    .map((rect) => `${rect.width}px ${rect.height}px`)
    .join(', ');
  const maskPosition = rects
    .map((rect) => `${rect.left}px ${rect.top}px`)
    .join(', ');
  const maskRepeat = rects.map(() => 'no-repeat').join(', ');

  return {
    maskImage,
    maskPosition,
    maskRepeat,
    maskSize,
    WebkitMaskImage: maskImage,
    WebkitMaskPosition: maskPosition,
    WebkitMaskRepeat: maskRepeat,
    WebkitMaskSize: maskSize,
  };
};

export const FloatingNameTitle: React.FC<FloatingNameTitleProps> = ({
  text,
  isVisible,
  onClick,
}) => {
  const titleRef = useRef<HTMLButtonElement>(null);
  const stackRef = useRef<HTMLSpanElement>(null);
  const textNodesRef = useRef<Text[]>([]);
  const rectCacheRef = useRef<WeakMap<Text, CharacterRectCacheEntry>>(
    new WeakMap()
  );
  const rafRef = useRef<number | null>(null);
  const [overlapRects, setOverlapRects] = useState<RectLike[]>([]);
  const [hopRun, setHopRun] = useState(0);

  const overlayMaskStyle = useMemo(
    () => buildOverlayMaskStyle(overlapRects),
    [overlapRects]
  );

  const restartHopAnimation = () => {
    setHopRun((currentRun) => currentRun + 1);
  };

  useEffect(() => {
    if (!titleRef.current) {
      return;
    }

    if (!stackRef.current) {
      return;
    }

    const stack = stackRef.current;

    gsap.set(stack, {
      opacity: 0,
      rotationX: -120,
      y: -100,
    });

    const entranceTween = gsap.to(stack, {
      duration: 1,
      opacity: 1,
      rotationX: 0,
      y: 0,
      transformOrigin: '50% 50%',
      ease: 'power2.out',
      delay: 0.2,
    });

    return () => {
      entranceTween.kill();
    };
  }, []);

  useEffect(() => {
    const clearOverlapRects = () => {
      setOverlapRects((current) => (current.length === 0 ? current : []));
    };

    if (!isVisible || !titleRef.current || !stackRef.current) {
      clearOverlapRects();
      return;
    }

    const floatingTitle = titleRef.current;
    const appRoot =
      (floatingTitle.closest('.App') as HTMLElement | null) ?? document.body;
    let isActive = true;

    const rebuildTextIndex = () => {
      rectCacheRef.current = new WeakMap();
      textNodesRef.current = collectVisibleTextNodes(appRoot, floatingTitle);
      scheduleUpdate();
    };

    const updateOverlapRects = () => {
      if (!isActive || !stackRef.current) {
        return;
      }

      const titleRect = toPlainRect(stackRef.current.getBoundingClientRect());

      if (titleRect.width <= 0 || titleRect.height <= 0) {
        clearOverlapRects();
        return;
      }

      const candidateRects = getCandidateCharacterRects(
        textNodesRef.current,
        titleRect,
        rectCacheRef.current,
        OVERLAP_PADDING
      );

      if (candidateRects.length === 0) {
        clearOverlapRects();
        return;
      }

      const nextRects = mergeRects(
        candidateRects
          .map((rect) => getIntersectionRect(rect, titleRect))
          .filter((rect): rect is RectLike => Boolean(rect))
          .filter(
            (rect) =>
              rect.width >= MIN_OVERLAP_WIDTH &&
              rect.height >= MIN_OVERLAP_HEIGHT
          )
          .map((rect) => translateRect(rect, titleRect)),
        RECT_MERGE_TOLERANCE
      );

      setOverlapRects((current) =>
        areRectListsEqual(current, nextRects) ? current : nextRects
      );
    };

    const scheduleUpdate = () => {
      if (!isActive || rafRef.current !== null) {
        return;
      }

      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        updateOverlapRects();
      });
    };

    rebuildTextIndex();

    const handleScroll = () => {
      scheduleUpdate();
    };

    const handleResize = () => {
      rectCacheRef.current = new WeakMap();
      scheduleUpdate();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    const observer = new MutationObserver(() => {
      rebuildTextIndex();
    });

    observer.observe(appRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const fontsReady = document.fonts?.ready;
    fontsReady?.then(() => {
      if (!isActive) {
        return;
      }

      rectCacheRef.current = new WeakMap();
      scheduleUpdate();
    });

    return () => {
      isActive = false;
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);

      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isVisible, text]);

  return (
    <button
      ref={titleRef}
      type="button"
      className={`floating-name-title${isVisible ? '' : ' is-hidden'}${
        hopRun > 0 ? ' is-hopping' : ''
      }`}
      onClick={onClick}
      onFocus={restartHopAnimation}
      onMouseEnter={restartHopAnimation}
      aria-label={text}
    >
      <span ref={stackRef} className="floating-name-title-stack">
        <span
          key={`base-${hopRun}`}
          aria-hidden="true"
          className="floating-name-title-copy floating-name-title-copy--base"
        >
          {renderAnimatedText(text)}
        </span>

        {overlayMaskStyle ? (
          <span
            key={`overlay-${hopRun}`}
            aria-hidden="true"
            className="floating-name-title-copy floating-name-title-copy--overlay"
            style={overlayMaskStyle}
          >
            {renderAnimatedText(text)}
          </span>
        ) : null}
      </span>
    </button>
  );
};
